import React, { useState, useEffect } from 'react';
import { useWeb3 } from '../context/Web3Context';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Alert, AlertDescription } from './ui/alert';
import { toast } from 'sonner';
import { 
    Mail, 
    Bell, 
    CheckCircle,
    Loader2,
    AlertTriangle,
    Info
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function EmailSettings() {
    const { address, isConnected } = useWeb3();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [serverEnabled, setServerEnabled] = useState(false);
    const [email, setEmail] = useState('');
    const [enabled, setEnabled] = useState(false);

    // Check if email is enabled on server
    useEffect(() => {
        async function checkEmailStatus() {
            try {
                const response = await fetch(`${API_URL}/api/email/status`);
                if (response.ok) {
                    const data = await response.json();
                    setServerEnabled(data.enabled);
                }
            } catch (error) {
                console.error('Failed to check email status:', error);
            }
        }
        checkEmailStatus();
    }, []);

    // Fetch user's email settings
    useEffect(() => {
        async function fetchSettings() {
            if (!address) {
                setLoading(false);
                return;
            }
            
            try {
                const response = await fetch(`${API_URL}/api/users/${address}/email-settings`);
                if (response.ok) {
                    const data = await response.json();
                    setEmail(data.email || '');
                    setEnabled(data.email_notifications || false);
                }
            } catch (error) {
                console.error('Failed to fetch email settings:', error);
            } finally {
                setLoading(false);
            }
        }
        fetchSettings();
    }, [address]);

    const handleSave = async () => {
        if (!address || !email) {
            toast.error('Please enter a valid email address');
            return;
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            toast.error('Please enter a valid email address');
            return;
        }

        setSaving(true);
        try {
            const response = await fetch(`${API_URL}/api/users/${address}/email-settings`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, enabled })
            });

            if (response.ok) {
                toast.success(enabled ? 'Email notifications enabled!' : 'Settings saved');
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to save settings');
            }
        } catch (error) {
            toast.error('Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    if (!isConnected) {
        return null;
    }

    return (
        <Card className="bg-white/[0.02] border-white/5">
            <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                    <Mail className="w-5 h-5 text-[#00FF66]" />
                    Email Notifications
                </CardTitle>
                <CardDescription className="text-white/50">
                    Get notified when your bets are accepted or resolved
                </CardDescription>
            </CardHeader>
            <CardContent>
                {!serverEnabled ? (
                    <Alert className="border-white/10 bg-white/5">
                        <Info className="w-4 h-4 text-white/60" />
                        <AlertDescription className="text-white/60 text-sm">
                            Email notifications are not configured on this server.
                            The platform administrator can enable them by setting up a Resend API key.
                        </AlertDescription>
                    </Alert>
                ) : loading ? (
                    <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-6 h-6 animate-spin text-[#00FF66]" />
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* Info about what gets notified */}
                        <Alert className="border-[#00FF66]/20 bg-[#00FF66]/5">
                            <Bell className="w-4 h-4 text-[#00FF66]" />
                            <AlertDescription className="text-white/60 text-sm">
                                You'll receive emails for:
                                <ul className="list-disc list-inside mt-1 space-y-0.5">
                                    <li>When someone accepts your bet</li>
                                    <li>When you win a bet</li>
                                    <li>When a bet is resolved</li>
                                    <li>When an oracle assertion is made</li>
                                </ul>
                            </AlertDescription>
                        </Alert>

                        {/* Email Input */}
                        <div className="space-y-2">
                            <label className="text-sm text-white/70">Email Address</label>
                            <Input
                                type="email"
                                placeholder="you@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                            />
                        </div>

                        {/* Enable Toggle */}
                        <div className="flex items-center justify-between p-3 rounded-lg bg-white/[0.02] border border-white/5">
                            <div className="flex items-center gap-3">
                                <Bell className="w-4 h-4 text-white/60" />
                                <span className="text-white/70 text-sm">Enable email notifications</span>
                            </div>
                            <Switch
                                checked={enabled}
                                onCheckedChange={setEnabled}
                            />
                        </div>

                        {/* Save Button */}
                        <Button
                            onClick={handleSave}
                            className="w-full bg-[#00FF66] text-[#0A0A0C] hover:bg-[#00CC52]"
                            disabled={saving || !email}
                        >
                            {saving ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                            ) : (
                                <><CheckCircle className="w-4 h-4 mr-2" />Save Settings</>
                            )}
                        </Button>

                        <p className="text-xs text-white/30 text-center">
                            Your email is only used for betting notifications and is never shared.
                        </p>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
